/**
 * PMS Module Utility Functions
 * Common utilities for KRA/KPI management components
 */

import React from "react";
import { Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import RateReviewIcon from "@mui/icons-material/RateReview";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ErrorIcon from "@mui/icons-material/Error";

/**
 * KRA Status types
 */
export type KRAStatus = 
  | "pending" 
  | "accepted" 
  | "awaiting_manager_review" 
  | "under_review"
  | "awaiting_hr_review" 
  | "completed"
  | "expired";

/**
 * Status configuration interface
 */
interface StatusConfig {
  label: string;
  color: "default" | "warning" | "success" | "info" | "primary" | "secondary";
  icon: JSX.Element;
}

/**
 * Status configurations for KRA workflows
 */
const statusConfigurations: Record<KRAStatus, StatusConfig> = {
  pending: {
    label: "Pending Acceptance",
    color: "warning",
    icon: <PendingIcon sx={{ fontSize: 16 }} />,
  },
  accepted: {
    label: "Accepted",
    color: "success",
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  },
  awaiting_manager_review: {
    label: "Awaiting Manager Review",
    color: "info",
    icon: <RateReviewIcon sx={{ fontSize: 16 }} />,
  },
  under_review: {
    label: "Under Review",
    color: "info",
    icon: <RateReviewIcon sx={{ fontSize: 16 }} />,
  },
  awaiting_hr_review: {
    label: "Awaiting HR Review",
    color: "secondary",
    icon: <AssignmentIcon sx={{ fontSize: 16 }} />,
  },
  completed: {
    label: "Completed",
    color: "primary",
    icon: <AssignmentTurnedInIcon sx={{ fontSize: 16 }} />,
  },
  expired: {
    label: "Period Expired",
    color: "default",
    icon: <ErrorIcon sx={{ fontSize: 16 }} />,
  },
};

/**
 * Get MUI Chip component for KRA status
 * @param status - KRA status
 * @returns MUI Chip component
 */
export const getStatusChip = (status: KRAStatus): JSX.Element => {
  const config = statusConfigurations[status] || statusConfigurations.pending;
  
  return (
    <Chip
      label={config.label}
      color={config.color}
      icon={config.icon}
      size="small"
      sx={{ fontWeight: "600" }}
    />
  );
};

/**
 * Format date string to readable format
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "Oct 5, 2025")
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * KRA Section interface for rating calculations
 */
interface KRASection {
  weightage: number;
  questions: Array<{
    weightage: number;
    rating_type?: 'numerical' | 'likert' | 'boolean';
    self_rating?: number | null;
    manager_rating?: number | null;
  }>;
}

/**
 * Calculate weighted average rating from KRA sections
 * Excludes Boolean (Yes/No) KPIs from score calculation - only Numerical and Likert KPIs contribute
 * Boolean KPIs are treated as compliance/checklist items and don't affect performance scores
 * Weights are normalized so the score remains on a 0-5 scale
 * @param sections - Array of KRA sections with questions and ratings
 * @param ratingField - Field name to use for rating ('self_rating' | 'manager_rating')
 * @returns Weighted average rating (0-5 scale)
 */
export const calculateWeightedRating = (
  sections: KRASection[],
  ratingField: 'self_rating' | 'manager_rating'
): number => {
  let totalWeightedRating = 0;
  let totalWeight = 0; // Track total weight of included KPIs (excluding Boolean)

  sections.forEach((section) => {
    section.questions.forEach((kpi) => {
      const rating = kpi[ratingField];
      const ratingType = kpi.rating_type || 'numerical'; // Default to numerical if not specified
      
      // Exclude Boolean KPIs from score calculation
      // Only include Numerical and Likert KPIs in performance score
      if (rating !== null && rating !== undefined && ratingType !== 'boolean') {
        const sectionWeight = section.weightage / 100;
        const kpiWeight = kpi.weightage / 100;
        const combinedWeight = sectionWeight * kpiWeight;
        totalWeightedRating += rating * combinedWeight;
        totalWeight += combinedWeight;
      }
    });
  });

  // Return weighted average (normalized by total weight of included KPIs)
  // This ensures the score remains on a 0-5 scale even when Boolean KPIs are excluded
  // If no KPIs are included (all Boolean), return 0
  if (totalWeight > 0) {
    return totalWeightedRating / totalWeight;
  }
  
  return 0;
};

/**
 * Convert rating to percentage
 * @param rating - Rating value (0-5 scale)
 * @param maxRating - Maximum rating value (default: 5)
 * @returns Percentage string with 1 decimal place
 */
export const ratingToPercentage = (rating: number, maxRating: number = 5): string => {
  return ((rating / maxRating) * 100).toFixed(1) + "%";
};

/**
 * Format rating display
 * @param rating - Rating value
 * @param maxRating - Maximum rating value (default: 5)
 * @returns Formatted rating string (e.g., "4.25/5.00")
 */
export const formatRating = (rating: number | null | undefined, maxRating: number = 5): string => {
  if (rating === null || rating === undefined) return "Not rated";
  return `${rating.toFixed(2)}/${maxRating.toFixed(2)}`;
};

/**
 * Convert rating value to text label based on rating type
 * @param rating - Rating value (0-5 scale)
 * @param ratingType - Type of rating system ('numerical' | 'likert' | 'boolean')
 * @returns Text label for the rating
 */
export const formatRatingLabel = (
  rating: number | null | undefined,
  ratingType?: 'numerical' | 'likert' | 'boolean'
): string => {
  if (rating === null || rating === undefined) {
    return "Not Rated";
  }

  // Convert to number if it's a string (handle edge cases)
  const numericRating = typeof rating === 'string' ? parseFloat(rating) : rating;
  
  if (isNaN(numericRating)) {
    return "Not Rated";
  }

  // Boolean: Yes/No
  if (ratingType === 'boolean') {
    return numericRating === 5 ? "Yes" : numericRating === 0 ? "No" : "Not Rated";
  }

  // Likert: Text labels
  if (ratingType === 'likert') {
    const likertMap: { [key: number]: string } = {
      5: "Outstanding",
      4: "Exceeds Expectations",
      3: "Meets Expectations",
      2: "Needs Improvement",
      1: "Unacceptable",
    };
    const roundedRating = Math.round(numericRating);
    return likertMap[roundedRating] || numericRating.toFixed(2);
  }

  // Numerical: Keep as number
  return numericRating.toFixed(2);
};

/**
 * Get performance grade based on percentage
 * @param percentage - Performance percentage (0-100)
 * @returns Object with grade and color
 */
export const getPerformanceGrade = (percentage: number): { grade: string; color: string } => {
  if (percentage >= 90) return { grade: "A - Excellent", color: "#2e7d32" };
  if (percentage >= 80) return { grade: "B - Very Good", color: "#1976d2" };
  if (percentage >= 70) return { grade: "C - Good", color: "#ed6c02" };
  if (percentage >= 60) return { grade: "D - Satisfactory", color: "#f57c00" };
  return { grade: "F - Needs Improvement", color: "#d32f2f" };
};

/**
 * Convert string to title case
 * @param str - String to convert
 * @returns Title cased string
 */
export const titleCase = (str: string | null | undefined): string => {
  if (str == null) return "";
  return str
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Get label for review period
 * @param period - Review period code (monthly, quarterly, half_yearly, yearly)
 * @returns Human-readable period label
 */
export const getPeriodLabel = (period: string): string => {
  const labels: Record<string, string> = {
    monthly: "Monthly (1 Month)",
    quarterly: "Quarterly (3 Months)",
    half_yearly: "Half-Yearly (6 Months)",
    yearly: "Yearly (12 Months)",
  };
  return labels[period] || period;
};

/**
 * Get short label for review period
 * @param period - Review period code
 * @returns Short period label (e.g., "Quarterly")
 */
export const getPeriodShortLabel = (period: string): string => {
  const labels: Record<string, string> = {
    monthly: "Monthly",
    quarterly: "Quarterly",
    half_yearly: "6 Months",
    yearly: "Yearly",
  };
  return labels[period] || period;
};

/**
 * Get color for days remaining warning
 * @param daysRemaining - Number of days remaining
 * @returns MUI color for chip/badge
 */
export const getPeriodDaysWarningColor = (daysRemaining: number): "error" | "warning" | "success" => {
  if (daysRemaining <= 7) return "error";
  if (daysRemaining <= 30) return "warning";
  return "success";
};

